// src/hooks/useGuide.ts
//
// Two concerns split across two hooks:
//
//   useGuideContent — listens to /users/{uid}/guide/current via Firestore
//     and returns the current guide payload. Used by GuidePage to render.
//
//   useGuideBackground — mounts once in <App> and drives the refresh
//     schedule: 2-min interval + tab-visibility changes + significant
//     location changes. Fires fire-and-forget POSTs to the refreshGuide
//     Cloud Function. Never blocks. Never awaits. The Firestore listener
//     in useGuideContent picks up the result asynchronously.
//
// The two hooks share nothing in memory. The Firestore doc is the shared
// state. This keeps the chat path and the guide path completely decoupled.

import { useEffect, useRef, useState, useCallback } from 'react';
import { doc, onSnapshot, Timestamp } from 'firebase/firestore';
import { auth, db } from '../services/firebase';
import { useLocation } from './useLocation';
import { useAuthStore } from '../stores/authStore';
import { getLanguageHint } from '../utils/getLanguageHint';

// ────────────────────────────────────────────────────────────
// Content listener
// ────────────────────────────────────────────────────────────

export interface GuideItem {
  title: string;
  description: string;
  distance_m?: number;
  walk_time_min?: number;
  tags: string[];
  /** The prompt that's sent to the chat when the user taps this item. */
  chat_prompt: string;
}

export interface GuideSection {
  id: string;
  title: string;
  /** Emoji the AI picked — rendered directly, no icon map needed. */
  emoji: string;
  /** Optional one-line reason why this section shows right now. */
  rationale?: string;
  items: GuideItem[];
}

export interface GuideContent {
  version: number;
  status: 'ready' | 'generating' | 'error';
  generated_at: Date | null;
  context_summary: string | null;
  sections: GuideSection[];
  last_error: string | null;
  address: string | null;
}

export const useGuideContent = () => {
  const [guide, setGuide] = useState<GuideContent | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      setIsLoading(false);
      return;
    }

    const ref = doc(db, 'users', user.uid, 'guide', 'current');
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setGuide(null);
          setIsLoading(false);
          return;
        }
        const data = snap.data();
        setGuide({
          version: data.version || 0,
          status: data.status || 'ready',
          generated_at:
            data.generated_at instanceof Timestamp
              ? data.generated_at.toDate()
              : null,
          context_summary: data.context_summary || null,
          sections: data.sections || [],
          last_error: data.last_error || null,
          address: data.generated_for?.address || null,
        });
        setIsLoading(false);
      },
      (err) => {
        console.warn('Guide listener error:', err);
        setIsLoading(false);
      }
    );

    return () => unsub();
  }, []);

  return { guide, isLoading };
};

// ────────────────────────────────────────────────────────────
// Background refresh scheduler
// ────────────────────────────────────────────────────────────

const REFRESH_INTERVAL_MS = 2 * 60 * 1000; // 2 minutes
const SIGNIFICANT_MOVE_METERS = 150;
const REFRESH_URL =
  'https://us-central1-travelx-46b7a.cloudfunctions.net/refreshGuide';

function haversine(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371e3;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const φ1 = toRad(lat1);
  const φ2 = toRad(lat2);
  const Δφ = toRad(lat2 - lat1);
  const Δλ = toRad(lon2 - lon1);
  const a =
    Math.sin(Δφ / 2) ** 2 +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Mount once near the top of the component tree (e.g. inside App.tsx).
 * Takes no props — consumes location from useLocation and auth state
 * from Firebase directly. Returns nothing useful; the side effect is
 * firing refresh requests on a schedule.
 */
export const useGuideBackground = () => {
  const { location } = useLocation();
  const profile = useAuthStore((s) => s.profile);
  const lastFiredLocationRef = useRef<{ lat: number; lng: number } | null>(
    null
  );
  const lastFiredAtRef = useRef<number>(0);
  const inFlightRef = useRef<boolean>(false);

  const fireRefresh = useCallback(
    async (loc: { latitude: number; longitude: number }, force = false) => {
      if (inFlightRef.current) return;
      const user = auth.currentUser;
      if (!user) return;

      // Local guard against hammering the endpoint. The server has its
      // own rate limit but we don't want to spam it.
      const sinceLast = Date.now() - lastFiredAtRef.current;
      if (!force && sinceLast < 30_000) return;

      inFlightRef.current = true;
      lastFiredAtRef.current = Date.now();
      lastFiredLocationRef.current = {
        lat: loc.latitude,
        lng: loc.longitude,
      };

      try {
        const token = await user.getIdToken();
        // Fire-and-forget — do not await the response body beyond the
        // HTTP ack. The server returns 202 immediately and does its
        // work in the background; content arrives via Firestore listener.
        fetch(REFRESH_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            auth_token: token,
            location: {
              latitude: loc.latitude,
              longitude: loc.longitude,
            },
            force,
            // Language hint for AI-generated section titles, item
            // descriptions, and prompt suggestions. Server prefers
            // travelProfile.demographics.primaryLanguage; this is the
            // fallback for anonymous / pre-onboarding users.
            language: getLanguageHint(profile),
          }),
        }).catch((err) => {
          console.warn('Guide refresh request failed:', err);
        });
      } finally {
        // Short in-flight window — 2 seconds is enough to prevent
        // duplicate requests from the same event loop tick, not to
        // wait for the server to finish (which can take 5-10s).
        setTimeout(() => {
          inFlightRef.current = false;
        }, 2000);
      }
    },
    [profile]
  );

  // ── Trigger 1: 2-minute interval while app is active ──
  useEffect(() => {
    if (!location) return;

    const tick = () => {
      if (document.visibilityState !== 'visible') return;
      if (!location) return;
      fireRefresh(location);
    };

    // Fire one immediately on mount
    tick();
    const id = window.setInterval(tick, REFRESH_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [location, fireRefresh]);

  // ── Trigger 2: tab becomes visible ──
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && location) {
        fireRefresh(location);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () =>
      document.removeEventListener('visibilitychange', onVisibility);
  }, [location, fireRefresh]);

  // ── Trigger 3: significant location change ──
  useEffect(() => {
    if (!location) return;
    const last = lastFiredLocationRef.current;
    if (!last) {
      // First location — will be covered by the interval's initial tick
      return;
    }
    const moved = haversine(
      last.lat,
      last.lng,
      location.latitude,
      location.longitude
    );
    if (moved >= SIGNIFICANT_MOVE_METERS) {
      fireRefresh(location, true);
    }
  }, [location, fireRefresh]);
};

/**
 * Manual refresh button. Returns a function that forces a refresh
 * regardless of rate limits.
 */
export const useGuideManualRefresh = () => {
  const { location } = useLocation();
  const profile = useAuthStore((s) => s.profile);
  const refreshing = useRef(false);

  return useCallback(async () => {
    if (refreshing.current) return;
    if (!location) return;
    const user = auth.currentUser;
    if (!user) return;

    refreshing.current = true;
    try {
      const token = await user.getIdToken();
      fetch(REFRESH_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          auth_token: token,
          location: {
            latitude: location.latitude,
            longitude: location.longitude,
          },
          force: true,
          // Same language hint as the background refresh path so a
          // manual refresh produces output in the same language as
          // the auto-generated content.
          language: getLanguageHint(profile),
        }),
      }).catch(() => {});
    } finally {
      setTimeout(() => {
        refreshing.current = false;
      }, 2000);
    }
  }, [location, profile]);
};
